var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Pagination } from '@mui/material';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { getPaginationData } from 'store/commonReducer/common.action';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { API_LOAD_STATE } from 'globals/enums';
var useRowStyles = makeStyles()(function (theme) { return ({
    ul: {
        paddingBottom: '2%',
        justifyContent: 'center',
        '& .Mui-selected': {
            color: theme.palette.customColor.white,
            background: theme.palette.customColor.primary,
        },
    },
}); });
/**
 * @description The actual pagination logic, as a plain, stably-identified
 * component. Render this directly - `<PaginationTable ... />` - rather than
 * through the `withPagination(...)` factory below when the parent may
 * re-render often (e.g. because of local sort/filter state). A factory call
 * produces a brand-new component type on every invocation, which forces
 * React to unmount/remount this component (re-running the initial-fetch
 * effect and showing the loader) on every parent re-render. Rendering
 * `PaginationTable` directly keeps one stable instance across re-renders,
 * so prop changes (like a new `comparator`, or a new `cacheKey` because
 * filters changed) just update it in place.
 *
 * `cacheKey` changes (e.g. from applying a filter) trigger a full reset:
 * page resets to 1, previously-loaded rows are cleared, and a fresh page-1
 * fetch is dispatched. `comparator` changes (sorting) only reset the page
 * and re-slice the already-loaded data - no fetch.
 */
export var PaginationTable = function (_a) {
    var WrappedComponent = _a.WrappedComponent, componentProps = _a.componentProps, getData = _a.getData, cacheKey = _a.cacheKey, rowsPerPage = _a.rowsPerPage, comparator = _a.comparator, filterFn = _a.filterFn;
    var classes = useRowStyles().classes;
    var dispatch = useAppDispatch();
    var tableData = useAppSelector(function (state) { return state.common.currentTableData.data; });
    var lastEvaluatedKey = useAppSelector(function (state) { return state.common.currentTableData.lastEvaluatedKey; });
    var isDataLoading = useAppSelector(function (state) { return state.common.currentTableData.loadingStatus; });
    var _b = React.useState(1), page = _b[0], setPage = _b[1];
    var _c = React.useState([]), rows = _c[0], setRows = _c[1];
    var expectingFreshData = React.useRef(false);
    var filteredSortedData = React.useMemo(function () {
        var result = filterFn ? tableData.filter(filterFn) : tableData;
        if (comparator) {
            result = __spreadArray([], result, true).sort(comparator);
        }
        return result;
    }, [tableData, filterFn, comparator]);
    /**
     * @description get total number of pages, based on the filtered dataset
     */
    var totalPages = React.useCallback(function () {
        return Math.ceil(filteredSortedData.length / rowsPerPage);
    }, [filteredSortedData, rowsPerPage]);
    var isFirstRender = React.useRef(true);
    React.useEffect(function () {
        if (isFirstRender.current) {
            isFirstRender.current = false;
        }
        else {
            expectingFreshData.current = true;
            setPage(1);
            setRows([]);
        }
        dispatch(getPaginationData(getData, rowsPerPage, cacheKey, 1));
    }, [cacheKey]);
    React.useEffect(function () {
        setPage(1);
    }, [comparator, filterFn]);
    React.useEffect(function () {
        if (expectingFreshData.current && isDataLoading === API_LOAD_STATE.PROGRESS) {
            return;
        }
        expectingFreshData.current = false;
        if (page) {
            var startRange = page * rowsPerPage;
            setRows(filteredSortedData.slice(startRange - rowsPerPage, startRange));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filteredSortedData, isDataLoading]);
    var handleChange = function (event, value) {
        setPage(value);
        if (value === totalPages() && lastEvaluatedKey) {
            dispatch(getPaginationData(getData, rowsPerPage, cacheKey, value));
        }
    };
    var modifiedComponentProps = React.useCallback(function () {
        return __assign(__assign({}, componentProps), { currentPage: page, isDataLoading: isDataLoading, data: rows });
    }, [componentProps, page, isDataLoading, rows]);
    return (_jsxs(React.Fragment, { children: [_jsx(WrappedComponent, __assign({}, modifiedComponentProps())), rows.length > 0 && (_jsx(Pagination, { classes: classes, count: totalPages(), page: page, onChange: handleChange }))] }));
};
/**
 * @description Legacy factory API, kept for any other existing usages of
 * this HOC elsewhere in the app. IMPORTANT: calling this inline in a render
 * body creates a new component type every render, which remounts the
 * pagination state and re-fetches. If the caller re-renders often (e.g. due
 * to local sort/filter state), render `<PaginationTable />` directly instead
 * - see SeniorCallLogs.tsx for the pattern.
 * @param {JSX.Element} WrappedComponent component
 * @param {Object} componentProps props of wrapped component
 * @param {Object} withPaginationProps props of HOC (getData, cacheKey,
 *   rowsPerPage, and an optional `comparator` to sort the full cached
 *   dataset before it's sliced into pages)
 * @returns {JSX.Element} JSX
 */
var withPagination = function (WrappedComponent, componentProps, withPaginationProps) {
    var WithPagination = function () { return (_jsx(PaginationTable, __assign({ WrappedComponent: WrappedComponent, componentProps: componentProps }, withPaginationProps))); };
    return WithPagination;
};
export default withPagination;
